import { type Ref, useId } from "react";

import { useUserSettings } from "../../app/userSettings";
import { HipaaBadge } from "../../components/ui/Badge";
import { SourceTile } from "../../components/ui/SourceTile";
import { formatClockRange } from "../../lib/dates";
import { sourceNames } from "../../lib/sources";
import { CheckIcon, PlusIcon } from "../../components/ui/Icons";
import { type CardBoard, PICKED, boardHandlers, onCardContextMenu } from "./cardBoard";
import { useDrafts } from "./drafts-context";
import { type BlockView, openTicketToAdd, takesNote } from "./model";
import { peekFacts } from "./peekFacts";
import { PEEK_HINT, PeekDetails } from "./PeekDetails";
import { TicketStateRow } from "./TicketStateRow";
import { ticketName } from "./tickets";
import type { CardDrag, DropHint } from "./useDragToMerge";
import { PEEK_OUTLINE, PEEK_ROOM, PRESS_DIP, PRESS_EASE, usePeek } from "./usePeek";

/** What it says, and whether a drop there does something. */
function noteOf(hint: DropHint): { text: string; ok: boolean } {
  if ("merging" in hint) {
    return { text: "Merging…", ok: true };
  }
  if ("logs" in hint) {
    // An open ticket (v0.9.0's P4): "Log 25m to ticket T… (open)", or why not.
    return { text: hint.hint, ok: hint.logs };
  }
  if (hint.merges) {
    return { text: hint.takes ? `Drop to merge. ${takesNote(hint.takes)}` : "Drop to merge", ok: true };
  }
  return { text: `Can't merge. ${hint.reason}`, ok: false };
}

/**
 * What a card, a dashed card or a tray row says while a block is dragged over
 * it: merging (ADR 0118), or logging to its open ticket (v0.9.0's P4).
 */
export function DropNote({ hint }: { hint: DropHint }) {
  const { text, ok } = noteOf(hint);
  // Opaque, over the card, and never in the way of the drag's own events.
  return (
    <div className="pointer-events-none absolute inset-0 z-[2] rounded-card bg-card">
      <p
        className={
          "flex h-full items-center justify-center rounded-card border-2 border-dashed px-4 text-center text-[13.5px] font-semibold " +
          (ok
            ? "border-brand text-brand-ink"
            : "border-status-needs-client-fg bg-status-needs-client-bg text-status-needs-client-fg")
        }
      >
        {text}
      </p>
    </div>
  );
}

// The chip's height, and the room kept for it at the foot of the card.
const CHIP_HEIGHT = "h-7";

/** A selected card's tick, on its corner: what says it's selected without colour alone. */
export function PickedBadge() {
  return (
    <span
      aria-hidden="true"
      data-part="picked-badge"
      className="pointer-events-none absolute -top-[7px] -right-[7px] z-[3] flex size-5 items-center justify-center rounded-pill bg-brand text-white shadow-[0_0_0_2px_var(--panel-2)]"
    >
      <CheckIcon />
    </span>
  );
}


/**
 * A block's card in its status column (section 11.0). A click opens the
 * block's popup (ADR 0184); while that's open, its card is the selected one,
 * solid Axon blue with white text. A card fades in when it first appears, and
 * flashes the logged green, which now means closed, when its ticket has just
 * closed (v0.9.0's P2b). A logged card says whether its ticket is open or
 * closed: its chip, and what it means beside it.
 *
 * Held still, it peeks (ADR 0193; `usePeek`): it opens up in place, outlined
 * in the brand colour as a pressed card is, its own background and text kept
 * (ADR 0194, decision 7), with its first three activities and the facts
 * (`peekFacts`), and folds when it's let go. Its draft notes are what the
 * tech typed in its Log form, saved or still waiting to be. The peek isn't a
 * selection: the card isn't pressed, and the ribbon is as it was.
 *
 * A dismissed card, shown with "Show dismissed", is faded (ADR 0058): its
 * background, border and source icons, with its text in the muted gray. Its
 * text keeps AA contrast, since the card is still a button (#243). Selected,
 * or peeking, it's drawn like any other.
 *
 * A card still to log can be dragged onto another to merge them (ADR 0118),
 * or onto an open ticket's to log it there (v0.9.0's P4). A peek folds as
 * the drag starts.
 *
 * Right-clicked, or with ☰ or Shift+F10, it has its menu; Ctrl+click,
 * Ctrl+Space, Shift+click and Shift+↑ ↓ select it among several, with the
 * brand ring, a faint tint and a tick (ADR 0194, decisions 3 and 5; the day
 * view's `board`). A selected card says it's pressed.
 *
 * A ready block that continues a logged one whose ticket is still open has a
 * chip, **"Add to open ticket T…?"** (v0.9.0's P4). It opens the block's
 * popup, its Log form on that ticket: nothing merges by itself. It's its own
 * button, beside the card's (a button can't hold one), drawn at the card's
 * foot, where the card keeps room for it. While the card peeks, its
 * "Continues" says it instead, as the prototype has it.
 */
export function BlockCard({
  block,
  timeZone,
  selected,
  onSelect,
  justClosed = false,
  now,
  ref,
  drag,
  board,
}: {
  block: BlockView;
  timeZone: string;
  selected: boolean;
  /** Opens the block's popup (ADR 0184): a click, Enter, or a tap of Space. */
  onSelect: (id: string) => void;
  /** Closed since this page last saw it: it flashes the logged green (section 11.0; v0.9.0's P2b). */
  justClosed?: boolean;
  /** The time now, for its ticket's age ("open 2 d"); the time it's drawn by default. */
  now?: Date;
  /** The card's button, to move focus to (#247). */
  ref?: Ref<HTMLButtonElement>;
  /** Dragging it onto another card, or another onto it, to merge them. */
  drag?: CardDrag;
  /** Its menu, and selecting several (ADR 0194): the day view's board. */
  board?: CardBoard | undefined;
}) {
  const { peekHoldMs } = useUserSettings();
  const drafts = useDrafts();
  const hintId = useId();
  const peek = usePeek({
    onOpen: () => {
      onSelect(block.id);
    },
    holdMs: peekHoldMs,
    ref,
  });
  // Selected: Axon blue. Peeking, it's outlined instead, on its own background.
  const lit = selected && !peek.peeking;
  const soft = lit ? "text-white/85" : "text-muted";
  const picked = board?.picked === true && !lit;
  const faded = block.kind === "dismissed" && !selected && !peek.peeking && !picked;
  const toAdd = peek.peeking ? null : openTicketToAdd(block);
  const at = now ?? new Date();
  return (
    <li
      className={
        "relative motion-safe:animate-block-in" +
        (drag?.dragging ? " opacity-60" : "")
      }
      data-just-closed={justClosed || undefined}
      draggable={drag?.draggable}
      onDragStart={(event) => {
        peek.onDragStart();
        drag?.onDragStart(event);
      }}
      onDragEnd={drag?.onDragEnd}
      onDragEnter={drag?.onDragEnter}
      onDragOver={drag?.onDragOver}
      onDragLeave={drag?.onDragLeave}
      onDrop={drag?.onDrop}
      onContextMenu={onCardContextMenu(board)}
    >
      <button
        type="button"
        aria-pressed={selected || picked}
        aria-describedby={peek.available ? hintId : undefined}
        data-peeking={peek.peeking || undefined}
        data-picked={picked || undefined}
        {...peek.button}
        {...boardHandlers(board, peek)}
        className={
          `flex w-full cursor-pointer flex-col gap-3.5 rounded-card border px-4 py-3.5 text-left ${PRESS_EASE} ` +
          (lit
            ? "bg-brand text-white shadow-[0_14px_30px_var(--brand-shadow)]"
            : faded
              ? "bg-card/60 text-muted"
              : "bg-card text-text") +
          // Pressed: the brand outline at once, and a slight dip (ADR 0193,
          // decision 4). Peeking: the outline, and a shadow (ADR 0194, decision 7).
          (peek.peeking
            ? ` ${PEEK_OUTLINE} ${PEEK_ROOM}`
            : picked
              ? ` ${PICKED}`
              : lit || peek.pressing
                ? " border-brand"
                : faded
                  ? " border-line/60 hover:border-brand"
                  : " border-line hover:border-brand") +
          (picked && peek.peeking ? " bg-[image:linear-gradient(var(--brand-tint),var(--brand-tint))]" : "") +
          (peek.pressing ? ` ${PRESS_DIP}` : "") +
          (justClosed ? " motion-safe:animate-logged-flash" : "")
        }
      >
        <span className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            <span className={"flex shrink-0 gap-[5px]" + (faded ? " opacity-60" : "")}>
              {block.lanes.map((lane) => (
                <SourceTile key={lane} lane={lane} onBrand={lit} decorative />
              ))}
            </span>
            {/* Its sources in words, beside their icons (Idea #525). */}
            <span className={`truncate text-[12.5px] font-medium ${soft}`}>{sourceNames(block.lanes)}</span>
          </span>
          <span className={`shrink-0 font-mono text-xs ${soft}`}>
            {formatClockRange(block.startedAt, block.endedAt, timeZone)}
          </span>
        </span>
        <span className="flex items-end justify-between gap-2.5">
          <span className="flex min-w-0 flex-col gap-[3px]">
            <span className="truncate text-base font-[650] tracking-[-0.015em]">{block.title}</span>
            <span
              className={
                "text-[12.5px] " +
                // Needs attention: Autotask didn't take it (#254).
                (block.kind === "not_logged" && !lit
                  ? "font-medium text-status-needs-client-fg"
                  : soft)
              }
            >
              {block.meta}
            </span>
          </span>
          <span className="text-[30px] leading-none font-semibold tracking-[-0.04em]">{block.duration}</span>
        </span>
        {block.ticket === null ? null : (
          <TicketStateRow ticket={block.ticket} now={at} timeZone={timeZone} onBrand={lit} />
        )}
        {block.isHipaa ? <HipaaBadge onBrand={lit} /> : null}
        {peek.peeking ? (
          <PeekDetails
            blocks={[block]}
            timeZone={timeZone}
            facts={peekFacts([block], {
              draftNotes: drafts.latest(block.id, block.draft).notes,
              now: at,
              timeZone,
            })}
          />
        ) : null}
        {/* Room for the chip, drawn over it below. */}
        {toAdd === null ? null : <span aria-hidden="true" className={`block ${CHIP_HEIGHT}`} />}
      </button>
      {peek.available ? (
        <span id={hintId} hidden>
          {PEEK_HINT}
        </span>
      ) : null}
      {toAdd === null ? null : (
        <button
          type="button"
          data-part="add-to-open-ticket"
          onClick={() => {
            onSelect(block.id);
          }}
          className={
            `absolute bottom-3.5 left-4 inline-flex ${CHIP_HEIGHT} max-w-[calc(100%-2rem)] cursor-pointer items-center gap-1.5 ` +
            "rounded-pill border pr-2.5 pl-2 text-[12.5px] font-semibold " +
            (selected
              ? "border-white/40 bg-card text-brand-ink-on-tint"
              : "border-status-in-progress-line bg-brand-tint text-brand-ink-on-tint hover:border-brand")
          }
        >
          <PlusIcon />
          <span className="truncate">{`Add to open ticket ${ticketName(toAdd)}?`}</span>
        </button>
      )}
      {picked ? <PickedBadge /> : null}
      {drag?.hint ? <DropNote hint={drag.hint} /> : null}
    </li>
  );
}
